// @mui
import { alpha } from '@mui/material/styles';
//
import palette from './palette';

// ----------------------------------------------------------------------

const themeColor = palette('light');

const LIGHT_MODE = themeColor.grey[500];

const DARK_MODE = themeColor.common.black;

/**
 * The function creates a set of shadow styles with different levels and colors.
 * @param color - The color parameter is a string representing the base color for the shadows. It is
 * used to generate transparent versions of the color for the different shadow levels.
 * @returns The function `createShadow` returns an object containing various CSS box-shadow values for
 * different levels of elevation (`z1`, `z4`, `z8`, etc.) as well as for specific use cases (`primary`,
 * `info`, `card`, etc.). The box-shadow values are generated based on the input `color` parameter
 * passed to the function.
 */
function createShadow(color) {
  const transparent = alpha(color, 0.16);
  return {
    z1: `0 1px 2px 0 ${transparent}`,
    z4: `0 4px 8px 0 ${transparent}`,
    z8: `0 8px 16px 0 ${transparent}`,
    z12: `0 12px 24px -4px ${transparent}`,
    z16: `0 16px 32px -4px ${transparent}`,
    z20: `0 20px 40px -4px ${transparent}`,
    z24: `0 24px 48px 0 ${transparent}`,
    //
    primary: `0 8px 16px 0 ${alpha(palette('light').primary.main, 0.24)}`,
    info: `0 8px 16px 0 ${alpha(palette('light').info.main, 0.24)}`,
    secondary: `0 8px 16px 0 ${alpha(palette('light').secondary.main, 0.24)}`,
    success: `0 8px 16px 0 ${alpha(palette('light').success.main, 0.24)}`,
    warning: `0 8px 16px 0 ${alpha(palette('light').warning.main, 0.24)}`,
    error: `0 8px 16px 0 ${alpha(palette('light').error.main, 0.24)}`,
    //
    card: `0 0 2px 0 ${alpha(color, 0.2)}, 0 12px 24px -4px ${alpha(color, 0.12)}`,
    dialog: `-40px 40px 80px -8px ${alpha(color, 0.24)}`,
    dropdown: `0 0 2px 0 ${alpha(color, 0.24)}, -20px 20px 40px -4px ${alpha(color, 0.24)}`,
  };
}

/**
 * The function returns a set of custom shadows based on the theme mode passed as an argument.
 * @param themeMode - The parameter `themeMode` is a string that specifies the current theme mode of
 * the application. It can have two possible values: 'light' or 'dark'.
 * @returns The function `customShadows` is being returned. It takes a parameter `themeMode` and
 * returns the result of a ternary operator. If `themeMode` is equal to `'light'`, it calls the
 * `createShadow` function with the `LIGHT_MODE` object as an argument. Otherwise, it calls the
 * `createShadow` function with the `DARK_MODE` object as an
 */
export default function customShadows(themeMode) {
  return themeMode === 'light' ? createShadow(LIGHT_MODE) : createShadow(DARK_MODE);
}
